import { addError } from '@infra/datadog/tracking';
import { DeviceIdService } from '@services/DeviceIdService';
import { TimeSyncService } from '@services/TimeSyncService';
import createClient from 'openapi-fetch';
import type { paths, components } from '@api/club/generated';

import { ApiError } from '@libs/errors';
import { SignalRSocket, SocketOptions } from '@libs/socket-signalr';

import { AuthService } from '@features/auth/services/AuthService';

export { components, paths };

const NAME = 'ClubApi';

/*
    ____,-------------------------------,____
    \   |          Http Client          |   /
    /___|-------------------------------|___\
*/

const api = createClient<paths>({
  baseUrl: __RUNTIME.clubApi,
});

export class UnauthorizedError extends Error {
  constructor(
    public url: string,
    message: string,
  ) {
    super(`Unauthorized. Url: ${url}, message: ${message}`);
  }
  name = 'UnauthorizedError';
}

api.use({
  async onRequest({ request }) {
    request.headers.set('X-Device-Id', DeviceIdService.instance.id);

    // SEE: https://openapi-ts.dev/openapi-fetch/middleware-auth
    if (AuthService.instance.accessToken) {
      request.headers.set('Authorization', `Bearer ${AuthService.instance.accessToken}`);
    }
    return request;
  },

  async onResponse({ response }) {
    if (response.status === 401) {
      // sic! если токен есть — он невалидный и надо его стереть
      AuthService.instance.deleteTokens();
    }

    if (response.status >= 400) {
      const err = await ApiError.fromResponse(NAME, response);
      const isInvalidPassword = err.isUnauthorized && response.url.includes('/auth/login');

      // не логируем неверные логины
      if (!isInvalidPassword) {
        addError(err);
      }
      throw err;
    }
  },

  onError({ error, request }) {
    const err = ApiError.fromRequestError(NAME, request, error);
    addError(err);
    throw err;
  },
});

const { GET, POST, DELETE } = api;
export { GET, POST, DELETE };

/*
    ____,-------------------------------,____
    \   |         Socket Clients        |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// User Socket

export const USER_SOCKET_URL = `${__RUNTIME.clubWs}/hubs/user`;

export type UserSocketEvents =
  // только свои
  | components['schemas']['GameStartsSoon']
  | components['schemas']['GameStarted']
  | components['schemas']['GameFinished']
  | components['schemas']['NoPair']
  | components['schemas']['SeanceStartsSoon']
  | components['schemas']['SeanceStarted']
  // все
  | components['schemas']['TournamentStartsSoon']
  | components['schemas']['RoundDrafted']
  | components['schemas']['RoundStarted']
  | components['schemas']['RoundFinished']
  | components['schemas']['TournamentPublished']
  | components['schemas']['TournamentUpdated']
  | components['schemas']['TournamentFinished']
  | components['schemas']['ParticipantsUpdated']
  | components['schemas']['ParticipantWasBanned']
  | components['schemas']['TeamWasBanned']
  | components['schemas']['ParticipantWasRemoved'];

export class UserSocket extends SignalRSocket<UserSocketEvents> {
  constructor(public url: string = USER_SOCKET_URL) {
    const EVENT_TYPES: Record<UserSocketEvents['eventType'], null> = {
      GameStartsSoon: null,
      GameStarted: null,
      GameFinished: null,
      NoPair: null,
      SeanceStartsSoon: null,
      SeanceStarted: null,
      //
      TournamentStartsSoon: null,
      RoundDrafted: null,
      RoundStarted: null,
      RoundFinished: null,
      TournamentPublished: null,
      TournamentUpdated: null,
      TournamentFinished: null,
      ParticipantsUpdated: null,
      ParticipantWasBanned: null,
      TeamWasBanned: null,
      ParticipantWasRemoved: null,
    };

    const options: SocketOptions<UserSocketEvents> = {
      accessTokenFactory:
        url === USER_SOCKET_URL ? AuthService.instance.socketAccessTokenFactory : undefined,

      onMsg: (e) => {
        TimeSyncService.instance.reportSocketTs(e.timestampMs);
      },
    };

    const u = new URL(url);

    if (AuthService.instance.user?.userId) {
      u.searchParams.append('userId', AuthService.instance.user?.userId);
    }

    super(u.toString(), Object.keys(EVENT_TYPES), options);

    const isPersonal = url.startsWith(USER_SOCKET_URL);
    if (isPersonal) {
      this.startOnlineReporting();
    }
  }

  startOnlineReporting() {
    this.subscribe((e) => {
      if (e.eventType === 'TournamentStartsSoon') {
        this.connection.send('IAmOnline', new Date().toISOString());
      }
    });
  }
}
